import { ApiBaseService, ApiV2Service } from './api.base.service';
import store from '@/store/store';
import { IdResponse, SigfoxApiCredentials, SuccessResponse, LoRaNetworkServer } from '@/types';

export interface SigfoxCredentials {
  id: number;
  login: string;
  sigfox_group: string;
  default_contract_id: string;
}

export interface LoRaCredentials {
  id: number;
  lns: string;
  username?: string;
  password?: string;
  customerId?: string;
  clusterId?: string;
  organisation_id?: string;
  application_server?: string;
  account_token?: string;
  application_id?: string;
  api_key?: string;
}

export class ConnectivityService {
  private api: ApiBaseService;

  constructor() {
    this.api = ApiV2Service;
  }

  public async getSigfoxApiCredentials(): Promise<SigfoxApiCredentials | null> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get<SigfoxApiCredentials | null>(`organisation/${organisationId}/network/sigfox/api`, { noToastFilters: [ { status: 404, gives: (r: any) => null }] });
  }

  public async createSigfoxApiCredentials(credentials: SigfoxApiCredentials): Promise<IdResponse> {
    const body = {
      managed: false,
      ...credentials,
    };
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.post(`organisation/${organisationId}/network/sigfox/api`, body  );
  }

  public async updateSigfoxApiCredentials(sigfoxApiId: string, credentials: SigfoxApiCredentials): Promise<IdResponse> {
    const body = {
      managed: false,
      ...credentials,
    };
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisationId}/network/sigfox/api/${sigfoxApiId}`, body);
  }

  public async deleteSigfoxApiCredentials(sigfoxApiId: string): Promise<SuccessResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.delete(`organisation/${organisationId}/network/sigfox/api/${sigfoxApiId}`);
  }

  public async getLoraSettings(): Promise<LoRaCredentials | null> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/network/lora`, { noToastFilters: [ { status: 404, gives: (r: any) => null }]});
  }

  public async hasGatewaySettings(): Promise<{result: boolean}> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/network/hasGatewaySettings`);
  }

  public async getLoRaNetworkServers(): Promise<LoRaNetworkServer[]> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/network/lora/servers`);
  }

  public async createLoRaSetting(lns: string, name?: string): Promise<IdResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    const body = { lns, name };
    const res: any = await this.api.post(`organisation/${organisationId}/network/lora/other`, body);
    return res && res.id ? { id: res.id } : { id: res };
  }

  public async createNetmoreSettings(body: { username: string; password: string; service_provider_code: string; name?: string }): Promise<IdResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    const res: any = await this.api.post(`organisation/${organisationId}/network/lora/netmore`, body);
    return res && res.id ? { id: res.id } : { id: res };
  }
  public async createIoTScotlandSettings(lns: string, username?: string, password?: string, customerId?: string, clusterId?: string, name?: string) {
    const organisationId = store.getters.activeOrganisation[0].id;
    const cust = customerId ? Number(customerId) : undefined;
    const clust = clusterId ? Number(clusterId) : undefined;
    const body = { lns, username, password, customer_id: cust, cluster_id: clust, name };
    const res: any = await this.api.post(`organisation/${organisationId}/network/lora/iotscotland`, body);
    return res && res.id ? { id: res.id } : { id: res };
  }

  public async createLoRaTTSSettings(lns: string, applicationServer: string, apiKey: string, organisationId: string, applicationId?: string, createWebhook: boolean = true, name?: string): Promise<any> {
    const orgId = store.getters.activeOrganisation[0].id;
    const body = { lns, application_id: applicationId, application_server: applicationServer, organisation_id: organisationId, api_key: apiKey, create_webhook: createWebhook, name };
    const res: any = await this.api.post(`organisation/${orgId}/network/lora/tts`, body);
    return res && res.id ? { id: res.id } : { id: res };
  }

  public async createLoriotSettings( settings: { host: string; application_id?: string; api_key: string, lns?: string, create_webhook?: boolean, name?: string } ): Promise<IdResponse> {
    const orgId = store.getters.activeOrganisation[0].id;
    settings.lns = 'loriot_managed';
    const res: any = await this.api.post(`organisation/${orgId}/network/lora/loriot`, settings);
    return res && res.id ? { id: res.id } : { id: res };
  }

  public async createThingParkSettings( settings: { network_server: string; client_id: string; client_secret: string, domain: string, create_webhook?: boolean, name?: string } ): Promise<IdResponse> {
    const orgId = store.getters.activeOrganisation[0].id;
    const res: any = await this.api.post(`organisation/${orgId}/network/lora/thingpark`, settings);
    return res && res.id ? { id: res.id } : { id: res };
  }

  public async updateLoRaSettings( lns: string, id: number, username?: string, password?: string): Promise<IdResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    const body = { lns, id, username, password };
    return this.api.put(`organisation/${organisationId}/network/lora/iotscotland`, body);
  }

  public async updateLoRaNetworkServer( lnsId: number, body: { name?: string; default?: boolean }): Promise<IdResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.put(`organisation/${organisationId}/network/lns/${lnsId}`, body);
  }

  public async updateTTISettings(lns: string, apiKey: string, id?: number) {
    const organisationId = store.getters.activeOrganisation[0].id;
    const body = { lns, id, api_key: apiKey };
    return this.api.put(`organisation/${organisationId}/network/lora/tti`, body);
  }

  public async removeLoRaSettings( id: number ): Promise<SuccessResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.delete(`organisation/${organisationId}/network/lora/${id}`);
  }

  public async hasNetworkSettings(setting: string): Promise<{result: boolean}> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.api.get(`organisation/${organisationId}/network/hasNetworkSettings?network=${setting}`);
  }
}

export default new ConnectivityService();
